import {
  useState,
  useEffect,
  ChangeEvent,
  Suspense,
  useRef,
} from 'react';
import Modal from 'react-bootstrap/Modal';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Button,
} from 'react-bootstrap';
import {
  useForm,
  SubmitHandler,
  Controller,
  FieldValues,
} from 'react-hook-form';
import MDPPositionTypeTable from './MDPPositionTypeTable';
import {
  EmploymentTypeData,
} from '../types/pdp.types';
import {
  MatchData,
} from '../types/mdp.types';
import {
  MIN_SALARY,
  MAX_SALARY,
  GAP_SALARY,
  CLC_FUNCTION,
} from '../types/constants';
import { tryParseInt } from '../utils';
import {
  currentRoleState,
  employmentTypeOptionsState,
  matchState,
} from '../services/state.service';
import SuspenseLoading from './generic/SuspenseLoading';
import { Role } from '../types/user.types';

type ManageMatchingModalProps = {
  callback: (_result: boolean, _match: MatchData) => void,
  show: boolean,
  hide: () => void,
  currentMatch: MatchData,
}

function ManageMatchingModal({
  callback,
  show,
  hide,
  currentMatch,
}: ManageMatchingModalProps): JSX.Element {
  const { t } = useTranslation();
  const [match, setMatch] = useRecoilState<MatchData>(matchState);
  const employmentTypes = useRecoilValue<EmploymentTypeData[]>(employmentTypeOptionsState);
  const [valid, setValid] = useState<boolean>(false);
  const currentRole = useRecoilValue<Role>(currentRoleState);
  const [advancedSearch, setAdvancedSearch] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
  } = useForm<MatchData>();

  const currentPositionType = watch('positionTypes');

  const submitRef = useRef<HTMLButtonElement>(null);

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    setMatch({ ...match, [name]: value, mdp: match.mdp });
  };

  const handlePresorStateSelectChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    if (value === '') {
      setMatch({ ...match, [name]: null, mdp: match.mdp });
      return;
    }
    setMatch({ ...match, [name]: value === 'true', mdp: match.mdp });
  };

  const handleLanguageChange = (language: string) => {
    setMatch((prevMatch: any) => ({
      ...prevMatch,
      matchLanguages: prevMatch.matchLanguages.includes(language)
        ? prevMatch.matchLanguages.filter((l: string) => l !== language)
        : [...prevMatch.matchLanguages, language],
    }));
  };

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    callback(true, { ...match, positionTypes: data.positionTypes });
  };

  useEffect(() => {
    reset(match);
  }, [show]);

  useEffect(() => {
    setAdvancedSearch(currentRole.functions.indexOf(CLC_FUNCTION.advancedSearch) > -1);
  }, [currentRole]);

  useEffect(() => {
    // reset form with user data
    if (match.id) reset(match);
  }, [match.id]);

  const handleClose = () => {
    reset(currentMatch);
  };

  useEffect(() => {
    if (
      match.employmentType
      && (currentPositionType && currentPositionType.length > 0)
      && match.maximumSalary
      && match.matchLanguages.length > 0
      && match.matchWithinPresbytery !== null
      && match.matchWithinState !== null
    ) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [match, currentPositionType]);

  return (
    <Modal size="lg" show={show} onHide={hide}>
      <Modal.Header closeButton />
      <Modal.Body className="text-center">
        <div className="col-lg-8 col-12 mx-auto">
          <div className="mx-auto">
            <div className="title">{t('Match_Criteria')}</div>
            <div className="col-lg-8 col-12 mx-auto">{t('Match_Description')}</div>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="my-3">
                  <Form.Group className="text-start col-6">
                    <Form.Label className="bodyheader required">
                      {t('Employment_Status')}
                    </Form.Label>
                    <Form.Select
                      {...register('employmentType')}
                      onChange={handleSelectChange}
                      disabled={!advancedSearch}
                    >
                      <option key="" value="">
                        {t('Select_Employment')}
                      </option>
                      {(employmentTypes.map((option: any) => (
                        <option key={`employment-type-${option.id}`} value={option.id}>
                          {option.description}
                        </option>
                      )))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="my-3">
                  <Form.Group className="text-start col-6">
                    <Form.Label className="bodyheader required">
                      {t('Match_Language')}
                    </Form.Label>
                    {'en,es,ko'.split(',').map((language: string) => (
                      <Controller
                        key={language}
                        name="matchLanguages"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="checkbox"
                            id={language}
                            label={t(`${language}`)}
                            checked={match.matchLanguages.includes(language)}
                            onChange={() => {
                              handleLanguageChange(language);
                              field.onChange(language);
                            }}
                          />
                        )}
                      />
                    ))}
                  </Form.Group>
                </div>
                <div className="my-3">
                  <Form.Group className="text-start col-12">
                    <Suspense fallback={<SuspenseLoading />}>
                      <MDPPositionTypeTable control={control} />
                    </Suspense>
                  </Form.Group>
                </div>
                <div className="my-3">
                  <Form.Group className="text-start col-12">
                    <Form.Label className="bodyheader required">
                      {t('Match_Maximum_Salary')}
                    </Form.Label>
                    <Form.Label className="bodyheader">
                      {`$${match.maximumSalary}`}
                    </Form.Label>
                    <Form.Range
                      value={match.maximumSalary}
                      min={MIN_SALARY}
                      max={MAX_SALARY}
                      step={GAP_SALARY}
                      {...register('maximumSalary', { required: true, valueAsNumber: true })}
                      onChange={(e: any) => setMatch((p: any) => ({
                        ...p, maximumSalary: tryParseInt(e.target.value),
                      }))}
                    />
                  </Form.Group>
                </div>
                <div className="my-3">
                  <Form.Group className="text-start col-12">
                    <Form.Check
                      type="checkbox"
                      id="no-matching"
                      label={t('No_Matching_Within_Presbytery')}
                      {...register('noMatchingWithinPresbytery')}
                      defaultChecked={currentMatch.noMatchingWithinPresbytery}
                      onChange={() => setMatch((p: any) => ({
                        ...p, noMatchingWithinPresbytery: !p.noMatchingWithinPresbytery,
                      }))}
                      disabled={match.noMatchingWithinPresbytery}
                    />
                  </Form.Group>
                </div>

                <div className="my-3">
                  <Form.Group className="text-start col-md-6 col-lg-4 col-12">
                    <Form.Label className="bodyheader required">
                      {t('Match_Within_Presbytery')}
                    </Form.Label>
                    <Form.Select
                      {...register('matchWithinPresbytery')}
                      onChange={handlePresorStateSelectChange}
                      disabled={!advancedSearch || match.noMatchingWithinPresbytery}
                    >
                      <option key="" value="">
                        {t('Select_Option')}
                      </option>
                      <option key="yes" value="true">
                        {t('Yes')}
                      </option>
                      <option key="no" value="false">
                        {t('No')}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="my-3">
                  <Form.Group className="text-start col-md-6 col-lg-4 col-12">
                    <Form.Label className="bodyheader required">
                      {t('Match_Within_State')}
                    </Form.Label>
                    <Form.Select
                      {...register('matchWithinState')}
                      onChange={handlePresorStateSelectChange}
                      disabled={!advancedSearch}
                    >
                      <option key="" value="">
                        {t('Select_Option')}
                      </option>
                      <option key="yes" value="true">
                        {t('Yes')}
                      </option>
                      <option key="no" value="false">
                        {t('No')}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
              <button
                ref={submitRef}
                type="submit"
                style={{ display: 'none' }}
                aria-label="submit"
              />
            </Form>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="danger"
          onClick={() => { callback(false, match); handleClose(); }}
        >
          {t('PDP.Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="col-2"
          onClick={() => submitRef.current?.click()}
          disabled={!valid}
        >
          {t('PDP.Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ManageMatchingModal;
